/**
 * Диалог настройки перед печатью.
 *
 * Триггерится Ctrl+P или кнопкой 🖨 в TopBar (RepoPage перехватывает оба).
 * После «Распечатать» открывает новую вкладку `/r/:owner/:repo/print?<opts>`,
 * где PrintPage сам вызовет `window.print()` после загрузки.
 */

import { useState } from 'react';
import { Button, Checkbox, Divider, Group, Modal, Radio, Stack, Text } from '@mantine/core';
import type { Snapshot } from '@pflow/shared/v2';
import { DEFAULT_PRINT_OPTIONS, HISTORY_DEPTH_OPTIONS, toSearchParams } from '../lib/print-options';
import type { HistoryDepth, PrintOptions } from '../lib/print-options';

interface Props {
    opened:      boolean;
    onClose:     () => void;
    snapshot:    Snapshot;
    currentRef:  string | undefined;
    currentPath: string | undefined;
}

export function PrintDialog({ opened, onClose, snapshot, currentRef, currentPath }: Props): JSX.Element {
    const [opts, setOpts] = useState<PrintOptions>(DEFAULT_PRINT_OPTIONS);

    const allCanvasNames = snapshot.canvases.map(c => c.name);
    // Пустой `opts.canvases` означает «все»; для UI это выглядит как все
    // чекбоксы помечены. Чтобы не путать пользователя, в момент первой
    // правки переключаемся на explicit-список.
    const selectedCanvases = opts.canvases.length === 0 ? allCanvasNames : opts.canvases;

    const handlePrint = () => {
        const params = toSearchParams(opts);
        if (currentRef)            params.set('ref',  currentRef);
        if (currentPath !== undefined) params.set('path', currentPath);
        const url = `/r/${snapshot.meta.owner}/${snapshot.meta.repo}/print?${params.toString()}`;
        window.open(url, '_blank', 'noopener');
        onClose();
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title="Печать / PDF документа"
            size="md"
            centered
        >
            <Stack gap="md">
                <Text fz="sm" c="dimmed">
                    Откроется новая вкладка с печатной версией — браузер сразу
                    предложит диалог печати или сохранения в PDF.
                </Text>

                <div>
                    <Text fw={600} fz="sm" mb={6}>Разделы</Text>
                    <Stack gap={6}>
                        <Checkbox
                            label="О проекте (обзор, рынок, рамки, что решили и что не решено)"
                            checked={opts.frame}
                            onChange={e => setOpts(o => ({ ...o, frame: e.currentTarget.checked }))}
                        />
                        <Checkbox
                            label="Поддержка (сигналы, план валидации)"
                            checked={opts.backstage}
                            onChange={e => setOpts(o => ({ ...o, backstage: e.currentTarget.checked }))}
                        />
                        <Checkbox
                            label="Здоровье модели (unfounded, orphans, stale-signals…)"
                            checked={opts.showHealth}
                            onChange={e => setOpts(o => ({ ...o, showHealth: e.currentTarget.checked }))}
                        />
                    </Stack>
                </div>

                {allCanvasNames.length > 0 && (
                    <div>
                        <Text fw={600} fz="sm" mb={6}>Канвасы</Text>
                        <Checkbox.Group
                            value={selectedCanvases}
                            onChange={(picked) => {
                                // Если выбраны все — сворачиваем обратно в пустой
                                // массив (значит «все», URL-параметра не будет).
                                const allPicked = picked.length === allCanvasNames.length;
                                setOpts(o => ({ ...o, canvases: allPicked ? [] : picked }));
                            }}
                        >
                            <Stack gap={4}>
                                {allCanvasNames.map(name => (
                                    <Checkbox key={name} value={name} label={`«${name}»`} />
                                ))}
                            </Stack>
                        </Checkbox.Group>
                    </div>
                )}

                <Divider />

                <div>
                    <Text fw={600} fz="sm" mb={6}>Глубина</Text>
                    <Radio.Group
                        value={opts.depth}
                        onChange={(v) => setOpts(o => ({ ...o, depth: v === 'summary' ? 'summary' : 'full' }))}
                    >
                        <Stack gap={4}>
                            <Radio value="summary" label="Только нарратив (LLM-summary)" />
                            <Radio value="full"    label="Нарратив + структурные данные (цитаты Сигналов/Развилок)" />
                        </Stack>
                    </Radio.Group>
                </div>

                <div>
                    <Text fw={600} fz="sm" mb={6}>Опции</Text>
                    <Stack gap={6}>
                        <Checkbox
                            label="Обложка (имя юнита, дата, ветка)"
                            checked={opts.cover}
                            onChange={e => setOpts(o => ({ ...o, cover: e.currentTarget.checked }))}
                        />
                        <Checkbox
                            label="Подпись «модель · дата» под summary"
                            checked={opts.showMeta}
                            onChange={e => setOpts(o => ({ ...o, showMeta: e.currentTarget.checked }))}
                        />
                    </Stack>
                </div>

                <div>
                    <Text fw={600} fz="sm" mb={6}>История изменений</Text>
                    <Text fz="xs" c="dimmed" mb={6}>
                        Последние коммиты, затрагивающие <code>.pflow/</code> и
                        <code> *.pflow</code>/<code>*.sflow</code> файлы — секция в конце документа.
                    </Text>
                    <Radio.Group
                        value={String(opts.historyDepth)}
                        onChange={(v) => {
                            const n = parseInt(v, 10);
                            const depth = (HISTORY_DEPTH_OPTIONS as readonly number[]).includes(n)
                                ? (n as HistoryDepth)
                                : 0;
                            setOpts(o => ({ ...o, historyDepth: depth }));
                        }}
                    >
                        <Stack gap={4}>
                            <Radio value="0"   label="Не включать" />
                            <Radio value="10"  label="Последние 10" />
                            <Radio value="20"  label="Последние 20" />
                            <Radio value="50"  label="Последние 50" />
                            <Radio value="100" label="Последние 100" />
                        </Stack>
                    </Radio.Group>
                </div>

                <Group justify="flex-end" mt="sm">
                    <Button variant="default" onClick={onClose}>Отмена</Button>
                    <Button onClick={handlePrint}>Распечатать</Button>
                </Group>
            </Stack>
        </Modal>
    );
}
